import _Object$keys from 'babel-runtime/core-js/object/keys';
import _Promise from 'babel-runtime/core-js/promise';
import _extends from 'babel-runtime/helpers/extends';
import _classCallCheck from 'babel-runtime/helpers/classCallCheck';
import _createClass from 'babel-runtime/helpers/createClass';
import renderTemplate from 'lodash.template';

import DEFAULTS from './constants';
import { InvalidResponseCode, NetworkError, RequestValidationError } from './errors';
import { isFunction, isObject, hasValue } from './typeChecks';
import { mergeConfig, serializeCookies } from './util';

var GenericResource = function () {
    /**
     * @param apiEndpoint Endpoint used for this resource. Supports ES6 token syntax, e.g: "/foo/bar/${pk}"
     * @param config Customize config for this resource (see `Router.config`)
     */
    function GenericResource(apiEndpoint, config) {
        _classCallCheck(this, GenericResource);

        this.apiEndpoint = apiEndpoint;

        // Set config
        this._customConfig = config;

        // set parent to null
        this._parent = null;
    }

    _createClass(GenericResource, [{
        key: '_setParent',
        value: function _setParent(parent) {
            this._parent = parent;
        }
    }, {
        key: 'config',
        value: function config() {
            var requestConfig = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;

            if (!this._config) {
                this._config = mergeConfig(this.parent ? this.parent.config() : DEFAULTS, this._customConfig);
            }

            if (requestConfig && isObject(requestConfig)) {
                return mergeConfig(this._config, requestConfig);
            }

            return this._config;
        }
    }, {
        key: 'setConfig',
        value: function setConfig(config) {
            // Update _customConfig
            this._customConfig = _extends({}, this._customConfig, config || {});

            // Reset _config so it is recreated in the next call to .config
            this.clearConfigCache();
        }
    }, {
        key: 'clearConfigCache',
        value: function clearConfigCache() {
            this._config = null;
        }
    }, {
        key: 'mutateRawResponse',
        value: function mutateRawResponse(rawResponse, requestConfig) {
            var config = this.config(requestConfig);
            if (isFunction(config.mutateRawResponse)) {
                return config.mutateRawResponse(rawResponse, requestConfig);
            }

            return rawResponse;
        }
    }, {
        key: 'mutateResponse',
        value: function mutateResponse(responseData, rawResponse, requestConfig) {
            var config = this.config(requestConfig);
            if (isFunction(config.mutateResponse)) {
                return config.mutateResponse(responseData, rawResponse, this, requestConfig);
            }

            return responseData;
        }
    }, {
        key: 'mutateError',
        value: function mutateError(error, rawResponse, requestConfig) {
            var config = this.config(requestConfig);
            if (isFunction(config.mutateError)) {
                return config.mutateError(error, rawResponse, this, requestConfig);
            }

            return error;
        }
    }, {
        key: 'getHeaders',
        value: function getHeaders() {
            var requestConfig = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;

            var config = this.config(requestConfig);
            var headers = _extends({}, this.parent ? this.parent.getHeaders() : {}, (isFunction(config.headers) ? config.headers() : config.headers) || {});

            var cookieVal = serializeCookies(this.getCookies(requestConfig));
            if (cookieVal) {
                headers.Cookie = cookieVal;
            }

            // if Accept is null/undefined, add default accept header automatically (backwards incompatible for text/html)
            if (!hasValue(headers.Accept)) {
                headers.Accept = config.defaultAcceptHeader;
            }

            return headers;
        }
    }, {
        key: 'getCookies',
        value: function getCookies() {
            var requestConfig = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;

            var config = this.config(requestConfig);
            return _extends({}, this.parent ? this.parent.getCookies() : {}, (isFunction(config.cookies) ? config.cookies() : config.cookies) || {});
        }
    }, {
        key: 'handleRequest',
        value: function handleRequest(req, requestConfig) {
            var _this = this;

            return this.ensureStatusAndJson(new _Promise(function (resolve) {
                var headers = _this.getHeaders(requestConfig);

                if (headers && isObject(headers)) {
                    _Object$keys(headers).forEach(function (key) {
                        if (hasValue(headers[key])) {
                            req = _this.setHeader(req, key, headers[key]);
                        }
                    });
                }

                _this.doRequest(req, function (response, error) {
                    return resolve(_this.constructor.wrapResponse(response, error, req));
                });
            }), requestConfig);
        }
    }, {
        key: 'ensureStatusAndJson',
        value: function ensureStatusAndJson(prom, requestConfig) {
            var _this2 = this;

            var config = this.config(requestConfig);
            return prom.then(function (origRes) {
                var res = _this2.mutateRawResponse(origRes, requestConfig);

                // If no error occured
                if (res && !res.hasError) {
                    if (config.statusSuccess.indexOf(res.status) !== -1) {
                        // Got statusSuccess response code, lets resolve this promise
                        return _this2.mutateResponse(res.data, res, requestConfig);
                    } else if (config.statusValidationError.indexOf(res.status) !== -1) {
                        // Got statusValidationError response code, lets throw RequestValidationError
                        throw _this2.mutateError(new RequestValidationError(res.status, res.text, config), res, requestConfig);
                    } else {
                        // Throw a InvalidResponseCode error
                        throw _this2.mutateError(new InvalidResponseCode(res.status, res.text), res, requestConfig);
                    }
                } else {
                    // res.hasError should only be true if network level errors occur (not statuscode errors)
                    var message = res && res.hasError ? res.error : '';

                    throw _this2.mutateError(new NetworkError(message || 'Something went awfully wrong with the request, check network log.'), res);
                }
            });
        }
    }, {
        key: 'buildThePath',
        value: function buildThePath(urlParams, requestConfig) {
            var thePath = this.apiEndpoint;
            var config = this.config(requestConfig);

            if (urlParams && !(isObject(urlParams) && _Object$keys(urlParams).length === 0)) {
                thePath = renderTemplate(this.apiEndpoint)(urlParams);
            }

            return '' + config.apiRoot + thePath;
        }

        /* istanbul ignore next */

    }, {
        key: 'createRequest',


        /* istanbul ignore next */
        value: function createRequest(method, url, query, data, requestConfig) {
            // eslint-disable-line class-methods-use-this, no-unused-vars
            throw new Error('Not implemented');
        }

        /* istanbul ignore next */

    }, {
        key: 'doRequest',
        value: function doRequest(req, resolve) {
            // eslint-disable-line class-methods-use-this, no-unused-vars
            throw new Error('Not implemented');
        }

        /* istanbul ignore next */

    }, {
        key: 'setHeader',
        value: function setHeader(req, key, value) {
            // eslint-disable-line class-methods-use-this, no-unused-vars
            throw new Error('Not implemented');
        }
    }, {
        key: 'parent',
        get: function get() {
            return this._parent;
        }
    }, {
        key: 'isBound',
        get: function get() {
            return !!this._parent || !!this._config;
        }
    }], [{
        key: 'wrapResponse',
        value: function wrapResponse(res, error, req) {
            // eslint-disable-line no-unused-vars
            throw new Error('Not implemented');
        }
    }]);

    return GenericResource;
}();

export default GenericResource;


//////////////////
// WEBPACK FOOTER
// ../~/tg-resources/es/base.js
// module id = 839
// module chunks = 0